export type PolicySettingsExample =
  | "normal"
  | "error"
  | "readonly"
  | "defaults";

/** The settings API owns values, defaults and whether a fallback is in use. */
export type PolicySettingRow = {
  key: string;
  value: unknown;
  default: unknown;
  description: string;
  isDefault: boolean;
};
export type PolicySettingsView = {
  settings: PolicySettingRow[];
  editable?: boolean;
};
export type PolicySettingDefinition = {
  label: string;
  description: string;
  unit?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  maxItems?: number;
};

export const policySettingDefinitions: Record<string, PolicySettingDefinition> =
  {
    bank_transfer_account: {
      label: "입금 계좌 안내",
      description: "고객의 결제 화면에 표시할 은행, 계좌번호와 예금주입니다.",
      maxLength: 300,
    },
    vat_rate_pct: {
      label: "부가세율",
      description: "청구서 발행 시 공급가액에 더할 세율입니다.",
      unit: "%",
      min: 0,
      max: 100,
    },
    invoice_due_days: {
      label: "청구서 납부 기한",
      description: "청구서 발행일부터 납부일까지의 기간입니다.",
      unit: "일",
      min: 1,
      max: 90,
    },
    annual_discount_months: {
      label: "연간 선불 할인",
      description:
        "연간 결제 금액에서 할인할 개월 수입니다. 0이면 할인하지 않습니다.",
      unit: "개월",
      min: 0,
      max: 6,
    },
    refund_window_days: {
      label: "청약철회 기간",
      description: "결제 후 청약철회를 신청할 수 있는 기간입니다.",
      unit: "일",
      min: 0,
      max: 30,
    },
    support_contact: {
      label: "운영 문의처",
      description:
        "고객에게 표시할 연락처입니다. 비우면 ‘담당자에게 문의’로 표시합니다.",
      maxLength: 300,
    },
    trial_grace_days: {
      label: "체험 종료 후 유예",
      description:
        "체험이 끝난 뒤 정지까지 기다리는 기간입니다. 0이면 바로 정지합니다.",
      unit: "일",
      min: 0,
      max: 30,
    },
    grace_days: {
      label: "미납 후 유예",
      description: "청구에 실패한 고객사를 정지하기 전까지의 기간입니다.",
      unit: "일",
      min: 0,
      max: 90,
    },
    lock_after_suspended_days: {
      label: "정지 후 잠금",
      description:
        "정지된 고객사에 2단계 제재인 잠금을 적용하기까지의 기간입니다.",
      unit: "일",
      min: 0,
      max: 365,
    },
    pause_timer_on_declared_transfer: {
      label: "입금 확인 중 정지 보류",
      description:
        "고객이 입금을 신고하면 관리자가 확인할 때까지 정지 타이머를 멈춥니다.",
    },
    transfer_pause_max_days: {
      label: "입금 신고 최대 보류 기간",
      description:
        "입금 확인이 늦어져도 이 기간을 넘겨 정지를 미루지 않습니다.",
      unit: "일",
      min: 1,
      max: 30,
    },
    task_quota_enforced: {
      label: "월 작업 한도 적용",
      description: "월 작업 횟수를 초과하면 새 작업을 보류합니다.",
    },
    quota_alert_pct: {
      label: "작업 한도 알림",
      description: "월 작업 한도를 이 비율만큼 사용하면 알립니다.",
      unit: "%",
      min: 1,
      max: 100,
    },
    task_count_rule: {
      label: "작업 횟수 산정 규칙",
      description:
        "AI 실행이 시작된 작업을 기준으로 월 작업 횟수를 계산합니다.",
    },
    held_max_age_hours: {
      label: "보류 작업 재개 기한",
      description: "보류 후 이 시간이 지나면 재개하지 않고 만료 처리합니다.",
      unit: "시간",
      min: 1,
      max: 720,
    },
    result_review_days: {
      label: "결과 검토 기한",
      description:
        "검토하지 않은 결과를 ‘유지’로 닫기까지 기다리는 기간입니다.",
      unit: "일",
      min: 1,
      max: 90,
    },
    result_screenshot_days: {
      label: "결과 캡처 보관",
      description: "게시가 끝난 결과 화면을 보관하는 기간입니다.",
      unit: "일",
      min: 7,
      max: 730,
    },
    run_retention_days: {
      label: "실행 이력 보관",
      description: "일반 실행 이력을 보관하는 기간입니다.",
      unit: "일",
      min: 7,
      max: 3650,
    },
    failed_run_retention_days: {
      label: "실패 이력 보관",
      description:
        "실패하거나 최종 실패 처리된 실행 이력을 보관하는 기간입니다.",
      unit: "일",
      min: 30,
      max: 3650,
    },
    llm_call_retention_days: {
      label: "AI 호출 이력 보관",
      description: "AI 호출, 토큰과 비용 이력을 보관하는 기간입니다.",
      unit: "일",
      min: 30,
      max: 2000,
    },
    seo_tracking_days: {
      label: "키워드 추적 기간",
      description: "마지막 작업 요청 이후 키워드 순위를 추적할 기간입니다.",
      unit: "일",
      min: 1,
      max: 730,
    },
    seo_collect_interval_hours: {
      label: "키워드 수집 주기",
      description: "같은 키워드의 검색 결과를 다시 수집하는 간격입니다.",
      unit: "시간",
      min: 1,
      max: 168,
    },
    seo_collect_batch: {
      label: "한 번에 수집할 키워드",
      description: "10분마다 진행하는 수집에서 처리할 최대 키워드 수입니다.",
      unit: "개",
      min: 1,
      max: 5000,
    },
    seo_followup_hours: {
      label: "게시 후 추가 수집",
      description:
        "게시 완료 후 추가로 확인할 시각을 쉼표로 구분합니다. 비우면 추가 수집을 끕니다.",
      unit: "시간",
      min: 1,
      max: 168,
      maxItems: 6,
    },
    seo_collect_depth: {
      label: "검색 결과 조회 범위",
      description:
        "상위 결과에서 조회할 개수입니다. 지역 검색은 최대 5개입니다.",
      unit: "개",
      min: 10,
      max: 100,
    },
    seo_quota_warn_pct: {
      label: "SEO API 한도 알림",
      description: "월 API 한도를 이 비율만큼 사용하면 알립니다.",
      unit: "%",
      min: 1,
      max: 100,
    },
    seo_result_retention_days: {
      label: "검색 결과 보관",
      description: "수집한 검색 결과를 보관하는 기간입니다.",
      unit: "일",
      min: 7,
      max: 730,
    },
    seo_trend_interval_days: {
      label: "검색 수요 수집 주기",
      description:
        "데이터랩 검색어 트렌드와 쇼핑인사이트를 수집하는 간격입니다.",
      unit: "일",
      min: 1,
      max: 90,
    },
    seo_trend_batch: {
      label: "검색 수요 수집 고객사",
      description: "매시간 처리할 고객사 수입니다. 0이면 수집을 끕니다.",
      unit: "개",
      min: 0,
      max: 500,
    },
    seo_trend_keywords: {
      label: "고객사별 검색 수요 키워드",
      description: "최근 요청한 키워드부터 확인하며 5개씩 묶어 요청합니다.",
      unit: "개",
      min: 1,
      max: 100,
    },
    seo_keyword_tool_interval_days: {
      label: "키워드 도구 수집 주기",
      description:
        "공용 키워드의 검색광고 도구 정보를 다시 수집하는 간격입니다.",
      unit: "일",
      min: 1,
      max: 90,
    },
    seo_keyword_tool_batch: {
      label: "키워드 도구 조회량",
      description: "매시간 조회할 키워드 수입니다. 0이면 수집을 끕니다.",
      unit: "개",
      min: 0,
      max: 2000,
    },
    seo_page_analyze_top: {
      label: "경쟁 분석 대상 글",
      description: "키워드마다 본문을 확인할 상위 글 수입니다. 0이면 끕니다.",
      unit: "개",
      min: 0,
      max: 30,
    },
    seo_page_fetch_batch: {
      label: "경쟁 분석 수집량",
      description: "한 번의 수집에서 가져올 글 수입니다.",
      unit: "개",
      min: 0,
      max: 500,
    },
    seo_page_refresh_days: {
      label: "경쟁 글 재확인 주기",
      description: "수집했던 글의 내용을 다시 확인하는 간격입니다.",
      unit: "일",
      min: 1,
      max: 90,
    },
    seo_page_retention_days: {
      label: "경쟁 글 보관",
      description:
        "검색 결과에 더 이상 나타나지 않는 글을 보관하는 기간입니다.",
      unit: "일",
      min: 7,
      max: 365,
    },
    llm_relay_enabled: {
      label: "API 키 방식 사용",
      description: "API 키를 사용하는 머신에 작업을 배정할지 정합니다.",
    },
    llm_relay_model: {
      label: "기본 AI 모델",
      description: "키별로 선택한 사용 모델 중 기본값입니다.",
    },
    llm_relay_reasoning_effort: {
      label: "추론 강도",
      description: "API 키 방식의 기본 추론 강도입니다.",
    },
    llm_prices: {
      label: "모델 단가",
      description:
        "AI 키 화면에서 모델별 입력, 캐시 입력, 출력 단가를 확인합니다.",
    },
    llm_prices_meta: {
      label: "단가 출처",
      description: "모델 단가의 수집 방식과 확인 시각입니다.",
    },
  };

export const policyFlagLabels: Record<string, string> = {
  countFailed: "실패한 작업도 1건으로 센다",
  countCancelledAfterStart: "시작한 뒤 취소된 작업도 센다",
  uncountSystemFailure: "시스템 장애로 실패한 작업은 세지 않는다",
};

export const policyGroups = [
  {
    id: "billing",
    title: "결제·청구",
    description: "납부 안내와 청구 기준",
    keys: [
      "bank_transfer_account",
      "vat_rate_pct",
      "invoice_due_days",
      "annual_discount_months",
      "refund_window_days",
      "support_contact",
    ],
  },
  {
    id: "sanctions",
    title: "정지·제재",
    description: "유예 기간과 서비스 제한",
    keys: [
      "trial_grace_days",
      "grace_days",
      "lock_after_suspended_days",
      "pause_timer_on_declared_transfer",
      "transfer_pause_max_days",
    ],
  },
  {
    id: "quota",
    title: "작업 횟수·한도",
    description: "횟수 산정과 초과 처리",
    keys: [
      "task_quota_enforced",
      "quota_alert_pct",
      "task_count_rule",
      "held_max_age_hours",
    ],
  },
  {
    id: "retention",
    title: "결과 검토·보관 기간",
    description: "결과 확인과 이력 보존",
    keys: [
      "result_review_days",
      "result_screenshot_days",
      "run_retention_days",
      "failed_run_retention_days",
      "llm_call_retention_days",
    ],
  },
  {
    id: "seo",
    title: "SEO 수집",
    description: "검색 성과와 경쟁 분석 수집",
    prefix: "seo_",
  },
  {
    id: "ai",
    title: "AI (API 키 방식)",
    description: "AI 키 화면에서 관리하는 설정",
    prefix: "llm_",
  },
  { id: "other", title: "기타", description: "그 밖의 운영 설정" },
] as const;

const defaults: Record<string, unknown> = {
  bank_transfer_account: "",
  vat_rate_pct: 10,
  invoice_due_days: 7,
  annual_discount_months: 2,
  refund_window_days: 7,
  support_contact: "",
  trial_grace_days: 0,
  grace_days: 7,
  lock_after_suspended_days: 14,
  pause_timer_on_declared_transfer: true,
  transfer_pause_max_days: 7,
  task_quota_enforced: false,
  quota_alert_pct: 80,
  task_count_rule: {
    countFailed: true,
    countCancelledAfterStart: true,
    uncountSystemFailure: true,
  },
  held_max_age_hours: 24,
  result_review_days: 7,
  result_screenshot_days: 90,
  run_retention_days: 90,
  failed_run_retention_days: 365,
  llm_call_retention_days: 400,
  seo_tracking_days: 90,
  seo_collect_interval_hours: 24,
  seo_collect_batch: 200,
  seo_followup_hours: [1, 6, 24],
  seo_collect_depth: 100,
  seo_quota_warn_pct: 80,
  seo_result_retention_days: 90,
  seo_trend_interval_days: 7,
  seo_trend_batch: 10,
  seo_trend_keywords: 20,
  seo_keyword_tool_interval_days: 7,
  seo_keyword_tool_batch: 100,
  seo_page_analyze_top: 10,
  seo_page_fetch_batch: 40,
  seo_page_refresh_days: 7,
  seo_page_retention_days: 60,
  llm_relay_enabled: true,
  llm_relay_model: "",
  llm_relay_reasoning_effort: "",
  llm_prices: {},
  llm_prices_meta: {},
};

/** Design fixtures only. A supplied view never mixes in these values. */
export function policySettingsFixture(
  example: PolicySettingsExample = "normal",
): PolicySettingsView {
  const overrides: Record<string, unknown> =
    example === "defaults"
      ? {}
      : {
          bank_transfer_account: "입금 계좌는 운영 담당자에게 문의해 주세요.",
          support_contact: "카카오톡 채널 · Meta Crew 운영팀",
          invoice_due_days: 14,
          llm_relay_model: "gpt-6.1-sol",
          llm_relay_reasoning_effort: "medium",
          llm_prices: {
            "gpt-6.1-sol": { input: 0, cachedInput: 0, output: 0 },
          },
          llm_prices_meta: {
            "gpt-6.1-sol": { source: "auto", checkedAt: null },
          },
        };
  return {
    editable: example !== "readonly",
    settings: Object.entries(defaults).map(([key, fallback]) => ({
      key,
      value: structuredClone(
        Object.hasOwn(overrides, key) ? overrides[key] : fallback,
      ),
      default: structuredClone(fallback),
      description: policySettingDefinitions[key].description,
      isDefault: !Object.hasOwn(overrides, key),
    })),
  };
}
